import React from 'react';

import './styles.scss';
import classNames from 'classnames';

import playPause from '../../utils/playPause';
import {onAudioUnlocked, unlockAudioNow, isAudioUnlocked} from '../../utils/unlockAudio';

import getUniqueId from '../../utils/getUniqueId';

import {DASHARRAY_LENGTH} from '../../constants';

import '../../actions';
import strings from '../../utils/strings';

class AudioPlayer extends React.Component {
	constructor(props) {
		super(props);

		this.uniqueId = `audio${getUniqueId()}`;
		this.retryCount = 0;

		this.state = {
			isPlaying: false,
			duration: 0,
			currentTime: 0,
			isDone: false,
			isStarted: false,
			needsTap: false,
			isLoading: Boolean(props.autoplay),
		};

		this.isReadyToPlay = false;
		this.pendingStart = false;

		this.tick = this.tick.bind(this);
		this.handleStartPlayer = this.handleStartPlayer.bind(this);
		this.handlePause = this.handlePause.bind(this);
		this.handlePlay = this.handlePlay.bind(this);
		this.handleLoadedMetadata = this.handleLoadedMetadata.bind(this);
		this.handleCanPlayThrough = this.handleCanPlayThrough.bind(this);
		this.handleError = this.handleError.bind(this);
	}

	componentDidMount() {
		const {audioElement, autoplay, preload} = this.props;

		if (audioElement) {
			// Adopt an externally-managed audio element.
			// The parent is responsible for calling play(); we just observe and reflect state.
			this.audioRef = audioElement;
			audioElement.addEventListener('loadedmetadata', this.handleLoadedMetadata);
			audioElement.addEventListener('canplaythrough', this.handleCanPlayThrough);
			audioElement.addEventListener('error', this.handleError);
			audioElement.addEventListener('play', this.handlePlay);
			audioElement.addEventListener('pause', this.handlePause);

			// Sync initial state — element may already be playing
			if (!audioElement.paused) {
				this.setState({isPlaying: true, isLoading: false}, () => this.tick());
			} else {
				const ready = audioElement.readyState >= 3;
				this.isReadyToPlay = ready;
				this.setState({isLoading: !ready});
			}

			if (audioElement.duration && !Number.isNaN(audioElement.duration)) {
				this.setState({duration: audioElement.duration, currentTime: audioElement.currentTime || 0});
			}
			return;
		}

		if (!this.audioRef) {
			return;
		}

		this.audioRef.addEventListener('loadedmetadata', this.handleLoadedMetadata);
		this.audioRef.addEventListener('canplaythrough', this.handleCanPlayThrough);
		this.audioRef.addEventListener('error', this.handleError);

		const actualPreload = autoplay ? 'auto' : (preload || 'none');
		this.audioRef.preload = actualPreload;

		if (autoplay || actualPreload === 'auto') {
			this.audioRef.load();
		}

		if (autoplay) {
			this.pendingStart = true;
			// isLoading is already true from the constructor — no setState needed here.

			// Call play() immediately while still inside the iOS user-activation
			// context (this runs synchronously in the same task as the triggering
			// gesture). iOS will queue the play and start it once buffered, without
			// needing another gesture. canplaythrough serves as a fallback.
			const immediatePlay = this.audioRef.play();
			if (immediatePlay) {
				immediatePlay.then(() => {
					// Play started — canplaythrough must not try to play again.
					this.pendingStart = false;
				}).catch((err) => {
					if (err.name === 'NotAllowedError') {
						this.pendingStart = false;
						this.setState({needsTap: true, isLoading: false});
						// Only register an auto-retry if audio hasn't been unlocked yet
						// (desktop / Android policy case). On iOS the unlock token is set
						// synchronously before AudioPlayers mount, so isAudioUnlocked()
						// is already true here — registering would fire cb() immediately
						// and reset needsTap: false before the ring ever renders.
						// Instead, leave needsTap: true so the user can tap the button.
						if (!isAudioUnlocked()) {
							onAudioUnlocked(() => {
								if (this.audioRef) {
									this.setState({needsTap: false});
									this.audioRef.play().catch(() => {});
								}
							});
						}
					}
					// AbortError or other: pendingStart stays true, canplaythrough retries.
				});
			}
		}
	}

	componentDidUpdate(prevProps) {
		const {src, autoplay, audioElement} = this.props;

		// External element is managed by the parent — ignore src changes.
		if (audioElement) return;

		if (prevProps.src !== src && this.audioRef) {
			cancelAnimationFrame(this.animationFrame);

			this.isReadyToPlay = false;
			this.pendingStart = Boolean(autoplay);
			this.retryCount = 0;

			// eslint-disable-next-line react/no-did-update-set-state
			this.setState({
				isPlaying: false,
				isDone: false,
				currentTime: 0,
				duration: 0,
				needsTap: false,
				isLoading: Boolean(autoplay),
			});

			this.audioRef.load();
		}
	}

	componentWillUnmount() {
		cancelAnimationFrame(this.animationFrame);

		if (this.audioRef) {
			this.audioRef.removeEventListener('loadedmetadata', this.handleLoadedMetadata);
			this.audioRef.removeEventListener('canplaythrough', this.handleCanPlayThrough);
			this.audioRef.removeEventListener('error', this.handleError);
			// These are no-ops for internal elements (React handles onPlay/onPause),
			// but necessary for external elements.
			this.audioRef.removeEventListener('play', this.handlePlay);
			this.audioRef.removeEventListener('pause', this.handlePause);
		}
	}

	handleLoadedMetadata() {
		if (!this.audioRef) {
			return;
		}

		const duration = this.audioRef.duration;
		if (duration && !Number.isNaN(duration)) {
			this.setState({currentTime: 0, duration});
		}
	}

	handleCanPlayThrough() {
		this.isReadyToPlay = true;
		this.setState({isLoading: false});

		if (this.pendingStart && this.audioRef && this.audioRef.paused) {
			// Only play if the immediate play() in componentDidMount didn't already start.
			this.pendingStart = false;
			playPause(this.audioRef, this.props.refs, () => {
				this.setState({needsTap: true});
				onAudioUnlocked(() => {
					if (this.audioRef) {
						this.setState({needsTap: false});
						this.audioRef.play().catch(() => {});
					}
				});
			});
		} else {
			this.pendingStart = false;
		}
	}

	handleError() {
		if (!this.audioRef || this.retryCount >= 2) {
			return;
		}
		this.retryCount += 1;
		if (this.state.isPlaying || this.pendingStart) {
			this.pendingStart = true;
		}
		this.audioRef.load();
	}

	tick() {
		const audioRef = this.audioRef;
		if (!audioRef) {
			return;
		}

		const hasStoppedPlaying = audioRef.ended || audioRef.paused;

		if (hasStoppedPlaying) {
			this.setState({isDone: true});
			return;
		}

		this.setState({currentTime: audioRef.currentTime});
		this.animationFrame = requestAnimationFrame(this.tick);
	}

	completeCallback() {
		if (this.props.onComplete) {
			this.props.onComplete();
		}
	}

	handlePlay() {
		cancelAnimationFrame(this.animationFrame);

		this.setState({isDone: false, isPlaying: true, needsTap: false, isLoading: false}, () => {
			this.tick();
		});

		if (typeof this.props.onStart === 'function') {
			this.props.onStart();
		}
	}

	handlePause() {
		const audioRef = this.audioRef;
		const isEnded = audioRef ? audioRef.ended : false;

		cancelAnimationFrame(this.animationFrame);

		this.setState({isPlaying: false, isDone: isEnded});

		if (isEnded) {
			this.completeCallback();
		}

		if (typeof this.props.onPause === 'function') {
			this.props.onPause();
		}
	}

	handleStartPlayer() {
		if (!this.audioRef) {
			return;
		}

		unlockAudioNow();
		this.setState({needsTap: false});

		if (this.isReadyToPlay || this.audioRef.readyState >= 3) {
			playPause(this.audioRef, this.props.refs);
			return;
		}

		this.pendingStart = true;
		this.audioRef.load();
	}

	render() {
		const {
			audioElement,
			disabled,
			id = this.uniqueId,
			inline,
			refs,
			src,
			preload = 'none',
		} = this.props;
		const {currentTime, duration, isPlaying, needsTap, isLoading} = this.state;

		let progress = 0;
		if (isPlaying && duration > 0) {
			const percentageDone = (currentTime * 100) / duration;
			progress = DASHARRAY_LENGTH - (percentageDone * DASHARRAY_LENGTH) / 100;
		}

		return (
			<button
				className={classNames({
					'lit-circle': true,
					'lit-audio': true,
					'is-playing': isPlaying,
					'needs-tap': needsTap,
					'is-loading': isLoading,
					inline: inline,
				})}
				disabled={disabled}
				onClick={() => {
					if (!this.audioRef) {
						return;
					}
					this.handleStartPlayer();
				}}
				title={disabled ? strings.audioMax : null}
			>
				<svg
					className="lit-circle__interactive"
					height="90"
					style={{enableBackground: 'new 0 0 90 90'}}
					version="1.1"
					viewBox="0 0 90 90"
					width="90"
					x="0px"
					xmlSpace="preserve"
					xmlns="http://www.w3.org/2000/svg"
					xmlnsXlink="http://www.w3.org/1999/xlink"
					y="0px"
				>
					<g transform="rotate(-84 45 45)">
						<circle
							className="lit-audio__outline"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="rgba(250, 248, 248, 0.99)"
							strokeLinecap="round"
							strokeWidth="5"
						/>
						<circle
							className="lit-audio__progress"
							cx="45"
							cy="45"
							fill="none"
							r="37"
							stroke="#f8fdfd"
							strokeLinecap="round"
							strokeWidth="6"
							style={{
								strokeDashoffset: `${progress}`,
							}}
						/>
					</g>
				</svg>
				<div className="lit-audio__icon" />
				{!audioElement && (
					<audio
						preload={preload}
						onPause={this.handlePause}
						onPlay={this.handlePlay}
						ref={(ref) => {
							this.audioRef = ref;
							if (refs && id) {
								refs[id] = ref;
							}
						}}
						src={src}
					/>
				)}
			</button>
		);
	}
}

export default AudioPlayer;